import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GitHubIcon from '@mui/icons-material/GitHub';
import ProviderCatalog from '@components/integrations/ProviderCatalog';
import AiProviderSection from '@components/integrations/AiProviderSection';
import { useTranslation } from 'react-i18next';
import { organizationService } from '@services/organizationService';
import { integrationService, type IntegrationConnection } from '@services/integrationService';
import { toDisplayError } from '../utils/apiError';

/**
 * REV-478: Settings → Integrations. Connect an external issue tracker (GitHub
 * Issues) so findings can be pushed out as tracked issues. The access token is
 * write-only: it is sent on connect and never returned by the API, so the list
 * only ever shows the repository and a "connected" state.
 *
 * Follows the platform "never pre-disable submit" rule: the Connect button is
 * enabled at all times and only disabled while a request is in flight.
 */
export default function IntegrationsPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const [orgId, setOrgId] = useState<string | null>(null);
  // REV-513: only admin+ may attach a credential that spends the org's money.
  const [canManageAi, setCanManageAi] = useState(false);
  const [connections, setConnections] = useState<IntegrationConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [owner, setOwner] = useState('');
  const [repo, setRepo] = useState('');
  const [token, setToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // REV-492: the connect form lives in a dialog, opened from a provider card.
  const [connectOpen, setConnectOpen] = useState(false);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setLoadError(null);
    try {
      const org = await organizationService.getBySlug(slug);
      setOrgId(org.id);
      setCanManageAi(org.role === 'owner' || org.role === 'admin');
      const list = await integrationService.list(org.id);
      setConnections(list);
    } catch (err) {
      setLoadError(
        toDisplayError(err, t('integrations.errors.loadFailed', 'Failed to load integrations'))
      );
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId) return;
    setFormError(null);
    setToast(null);

    // Validate on submit (never pre-disable the button).
    //
    // REV-514: a connection is AUTHENTICATION now — the repository lives on the
    // tracked place. The token is the only required field. Owner/repo remain
    // accepted as an optional default for places that have not chosen one, but
    // a half-filled pair is a typo, not an intent, so it is rejected rather
    // than silently dropped.
    if (!token.trim()) {
      setFormError(t('integrations.errors.tokenRequired', 'An access token is required.'));
      return;
    }
    if (Boolean(owner.trim()) !== Boolean(repo.trim())) {
      setFormError(
        t(
          'integrations.errors.repoPairRequired',
          'Enter both the repository owner and name, or leave both blank to connect the account only.'
        )
      );
      return;
    }

    setSubmitting(true);
    try {
      await integrationService.createGithub(orgId, {
        owner: owner.trim(),
        repo: repo.trim(),
        token: token.trim(),
      });
      setToast(t('integrations.connected', 'GitHub repository connected.'));
      setConnectOpen(false);
      // Never keep the secret in memory after a successful save.
      setOwner('');
      setRepo('');
      setToken('');
      await load();
    } catch (err) {
      setFormError(
        toDisplayError(
          err,
          t(
            'integrations.errors.connectFailed',
            'Could not connect. Check the repository and token.'
          )
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (connectionId: string) => {
    if (!orgId) return;
    setDeletingId(connectionId);
    setFormError(null);
    try {
      await integrationService.remove(orgId, connectionId);
      setToast(t('integrations.removed', 'Connection removed.'));
      await load();
    } catch (err) {
      setFormError(
        toDisplayError(err, t('integrations.errors.removeFailed', 'Failed to remove connection'))
      );
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress aria-label={t('common.loading', 'Loading...')} />
      </Box>
    );
  }

  return (
    // REV-692: a plain Box, like every other organization screen
    // (OrganizationDetailPage, ContextDetailPage). This page used
    // `p: 3, maxWidth: 720, mx: 'auto'`, which double-padded inside
    // MainLayout's own Container and squeezed the content into a narrow
    // centred column — so Integrations looked like a different product from
    // General, Members, Teams and Tracked Places, which is exactly what the
    // walkthrough reported.
    <Box>
      {/* REV-732: goes to a URL, so it is a link. */}
      <Button
        startIcon={<ArrowBackIcon />}
        component={RouterLink}
        to={`/orgs/${slug}`}
        sx={{ mb: 2 }}
      >
        {t('integrations.back', 'Back to organization')}
      </Button>

      <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
        {t('integrations.title', 'Integrations')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t(
          'integrations.subtitle',
          'Connect an issue tracker so you can push findings out as tracked issues.'
        )}
      </Typography>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError(null)}>
          {loadError}
        </Alert>
      )}
      {toast && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setToast(null)}>
          {toast}
        </Alert>
      )}

      {/* REV-492: provider catalog. Answers "what CAN I connect?" as well as
          "what have I connected?" — the old page only did the latter, so a user
          could not discover Jira existed. */}
      <ProviderCatalog
        orgId={orgId ?? ''}
        connections={connections}
        onConnect={() => {
          setFormError(null);
          setConnectOpen(true);
        }}
        onDisconnect={handleDelete}
        deletingId={deletingId}
      />

      {/* REV-513. Below the catalog, not inside it: every catalog entry means
          "push a finding somewhere", and an AI credential is a different kind
          of thing — the same separation the API keeps between
          org_ai_credentials and integration_connections. */}
      {orgId && <AiProviderSection orgId={orgId} canManage={canManageAi} />}

      <Dialog
        open={connectOpen}
        onClose={() => !submitting && setConnectOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('integrations.github.title', 'Connect GitHub Issues')}</DialogTitle>
        <Box component="form" onSubmit={handleConnect} noValidate>
          <DialogContent>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError(null)}>
                {formError}
              </Alert>
            )}
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label={t('integrations.github.owner', 'Repository owner (optional)')}
                helperText={t(
                  'integrations.github.ownerHelp',
                  'Optional default. Each tracked place chooses its own repository in its Settings.'
                )}
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                fullWidth
                autoComplete="off"
              />
              <TextField
                label={t('integrations.github.repo', 'Repository name (optional)')}
                helperText={t(
                  'integrations.github.repoHelp',
                  'Optional default, used only by tracked places that have not chosen a repository.'
                )}
                value={repo}
                onChange={(e) => setRepo(e.target.value)}
                fullWidth
                autoComplete="off"
              />
              <TextField
                label={t('integrations.github.token', 'Access token')}
                helperText={t(
                  'integrations.github.tokenHelp',
                  'A token with permission to create issues in the repositories you will target. It is encrypted and never shown again.'
                )}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                type="password"
                fullWidth
                autoComplete="off"
                inputProps={{ 'aria-label': t('integrations.github.token', 'Access token') }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConnectOpen(false)} disabled={submitting}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<GitHubIcon />}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <CircularProgress size={18} sx={{ mr: 1 }} color="inherit" />
                  {t('integrations.github.connecting', 'Connecting…')}
                </>
              ) : (
                t('integrations.github.connect', 'Connect repository')
              )}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}
